import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { LanguageSwitcher } from './LanguageSwitcher';

export function ThankYou() {
  const { translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get rating from URL params if available
  const ratingParam = searchParams.get('rating');
  const rating = ratingParam ? parseInt(ratingParam, 10) : null;

  const getRatingMessage = () => {
    if (!rating) {
      return translate('feedback.success.message');
    }
    const ratingMessages: Record<number, string> = {
      1: translate('feedback.success.ratingMessage.1'),
      2: translate('feedback.success.ratingMessage.2'),
      3: translate('feedback.success.ratingMessage.3'),
      4: translate('feedback.success.ratingMessage.4'),
      5: translate('feedback.success.ratingMessage.5'),
    };
    return ratingMessages[rating] || translate('feedback.success.message');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-card border border-border rounded-lg shadow-lg p-6 md:p-8 text-center">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>

          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-4">
              <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
            {translate('feedback.success.title')}
          </h1>

          {/* Rating Stars (if rating provided) */}
          {rating && (
            <div className="flex justify-center gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'w-8 h-8',
                    star <= rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'fill-gray-300 text-gray-300 dark:fill-gray-600 dark:text-gray-600'
                  )}
                />
              ))}
            </div>
          )}

          {/* Message */}
          <p className="text-lg text-muted-foreground mb-8">
            {getRatingMessage()}
          </p>

          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            {translate('feedback.thankYou.backToForm') || 'Submit Another Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}

